import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Input,
} from '@mui/material';
// Removed unused Redux Dispatch import

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * If you need “I Acknowledged” checkbox (used for delete) set requireAck = true.
   */
  reasons: string[];
  setReasons: React.Dispatch<React.SetStateAction<string[]>>;
}

const ApproveRejectDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onClose,
  setReasons,
  reasons,
}) => {
  const [ack, setAck] = React.useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleAddReason = () => {
    if (inputValue.trim() !== '') {
      setReasons([...reasons, inputValue.trim()]);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddReason();
    }
  };

  const handleRemoveReason = (index: number) => {
    setReasons(reasons.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setAck(false);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    setAck(false);
  };

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: { borderRadius: '24px', padding: '30px' },
      }}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center', fontSize: 24 }}>
        {title}
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center' }}>
        {typeof message === 'string' ? (
          <Typography sx={{ fontSize: '16px', lineHeight: '24px' }}>
            {message}
          </Typography>
        ) : (
          message
        )}

        <div className='bg-gray-100 rounded-xl p-4 my-6 text-left'>
          <p className='text-gray-700 text-sm mb-3 font-medium'>
            {title} Reason Keywords
          </p>
          <div className='flex flex-wrap gap-2 mb-3'>
            {reasons.map((kw, i) => (
              <span
                key={i}
                className='flex items-center gap-2 bg-white border px-3 py-1 rounded-full text-sm text-gray-800 shadow-sm'
              >
                {kw}
                <button
                  type='button'
                  onClick={() => handleRemoveReason(i)}
                  className='text-gray-500 hover:text-red-500'
                >
                  X
                </button>
              </span>
            ))}
          </div>
          <Input
            placeholder='Enter reason keyword'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

          <Box mt={2} display='flex' justifyContent='center'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                />
              }
              label='I Acknowledge'
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: 16,
                  color: 'black',
                },
              }}
            />
          </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
        <Button
          variant='outlined'
          disabled={!ack || reasons.length == 0}
          onClick={handleConfirm}
          sx={{
            textTransform: 'none',
            borderRadius: 100,
            px: 4,
            color: 'black',
            borderColor: 'black',
          }}
        >
          {confirmLabel}
        </Button>
        <Button
          variant='contained'
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            borderRadius: 100,
            px: 4,
            bgcolor: '#FF0050',
            '&:hover': { bgcolor: '#E6004F' },
          }}
        >
          {cancelLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveRejectDialog;
